import React, { useEffect, useRef, useState } from 'react';

import { useTheme } from '../../theme/ThemeContext';
import { LAB_CONTEXT_KIND } from '../../constants/patientCard';
import {
  groupVisitEntriesByAttendingDoctor,
  resolveSegmentDoctorName,
} from '../../utils/visitHistoryGrouping';
import { isProceduresTruncated } from '../../utils/patientCardProcedures';
import { readLane } from '../../utils/patientCardLanes';
import { hasPrescriptionContent } from '../../utils/prescriptionContent';
import { openPrescriptionPdf } from '../../services/api/prescriptionPdfApi';
import { downloadFileSecure } from '../../services/api/visitMetadataApi';
import useFileViewerStore from '../../stores/useFileViewerStore';
import { HistorySection } from '../visit/history/HistorySection';
import { FileRow } from '../visit/history/FileRow';
import { LaneSubsection } from './LaneSubsection';
import { VisitProceduresHistorySection } from '../visit/history/VisitProceduresHistorySection';
import { VisitLabHistorySection } from '../visit/history/VisitLabHistorySection';
import PrescriptionReadOnlyView from '../visit/PrescriptionReadOnlyView';

/** Same tiny byte-size formatter every other `FileRow` caller defines
 * locally (`PatientHistory.jsx`, `ManageVisitPage.jsx`, `VisitActionsPanel.jsx`)
 * — no shared util exists for this, so this follows the established
 * per-consumer convention rather than inventing one. */
function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Same empty/secondary line styling `LaneSubsection` uses, so the orphan
 *  group's explanatory line reads like the rest of the card. */
const HINT_LINE_CLASS = 'text-[0.6875rem] text-slate-500 dark:text-slate-400';

const PHOTOS_HEADING = '📷 Prescription photos';

/** The orphan group's copy (owner D27). It asserts EXACTLY the two things the
 *  rows carry — the visit they were added on (`visit_id`) and that no
 *  prescription in this record claims them — and nothing more. No parent is
 *  named, guessed or implied: the supersede chain is readable server-side and
 *  following it was deliberately rejected there, so the card must not do it
 *  either. */
const UNLINKED_PHOTOS_HEADING = '📷 Photos not linked to a prescription';
const UNLINKED_PHOTOS_HINT =
  "These photos were added on this visit. They can't be linked to a prescription in this record.";

/**
 * A prescription's photos (owner D27), rendered with the affordance this card
 * ALREADY uses for a file — `FileRow`, exactly as `reports` renders. Each item
 * is a `MetadataRecordItem` DESCRIPTOR (`file_name` / `content_type` /
 * `file_size`); the response carries no URL and this renders none. A presigned
 * URL is minted only by the per-entry download route, on an explicit click of
 * the row's "View" button — a card render must never touch a document endpoint
 * (bk-790).
 *
 * ARRAY ORDER, never re-sorted. The server decides it: a prescription's own
 * `images` ascend by `image_order`, `unattached_images` come newest-first.
 * Re-sorting here would fork that decision into a second, drifting rule.
 */
function PrescriptionPhotoRows({ images, isDark, onView }) {
  return images.map((image) => (
    <FileRow
      key={image.meta_id}
      entry={image}
      isDark={isDark}
      formatSize={formatFileSize}
      onView={() => onView(image)}
    />
  ));
}

/**
 * OpdTimelineRow (F7) — the expanded detail for a `TimelineRow` whose
 * `ref.kind === 'VISIT'`. See `TimelineRow.jsx`'s JSDoc for the frozen prop
 * interface this binds to.
 *
 * Everything renders from `sections` (the FULL clinical-record map already
 * resolved by the page's one aggregator call, §1.2) — this component issues
 * ZERO requests of its own on expand. The only network calls it can ever
 * trigger are explicit user actions inside the reused sub-components
 * (View prescription, View report, Collect payment, Done outside) — never on
 * mount, never on expand.
 *
 * Reused verbatim (no forked renderers, per the F1 extraction this task
 * exists to consume):
 *   - `PrescriptionReadOnlyView` — the visit's latest active digital
 *     prescription ("latest non-superseded digital entry WITH CONTENT", the
 *     shared `hasPrescriptionContent` rule — `VisitActionsPanel` and
 *     `DoctorQueueView` read the same rule as of bk-1149). When no
 *     digital entry qualifies, falls back to the latest non-superseded
 *     FILE-BACKED entry (scanned/uploaded — `s3_key` present) rendered via
 *     `FileRow` + `FileViewerModal` (F7-F fix 2 — a prescription that was
 *     scanned rather than typed is not "no prescription"). Passes
 *     `canGeneratePdf` (bk-861, D24) so the download button is reachable
 *     regardless of `GENERATE_PRESCRIPTION_PDF`, which excludes ADMIN —
 *     the PDF endpoint is already open to any org user server-side. `onEdit`
 *     is intentionally NOT passed: the card is read-only (D6), so the Edit
 *     Prescription action never renders here even for a member who holds
 *     `ADD_PRESCRIPTION`.
 *   - `FileRow` (via `PrescriptionPhotoRows`) — the photos attached to the
 *     prescription being shown (owner D27). They are NESTED on the item
 *     (`items[i].images`, always present, `[]` when none), never sibling rows,
 *     so `prescriptions.total`/`returned` still count PRESCRIPTIONS and this
 *     row never re-derives a count from `items.length + images.length`.
 *     Photos whose parent prescription is not in the response ride
 *     `prescriptions.unattached_images` and get their own explicitly-unlinked
 *     group — reachable TODAY, because correcting a prescription on a CLOSED
 *     visit leaves its photos naming the superseded version, which the
 *     response filters out (bk-1133). Skipping that group would keep every
 *     post-completion-edited prescription's photos invisible, which is the
 *     exact defect D27 exists to end.
 *   - `VisitProceduresHistorySection` — sittings performed on this visit.
 *   - `VisitLabHistorySection` (+ `LabReportActions`, `reportOnly` — a
 *     read-only reference card never mutates a lab order's payment/external
 *     state from a stale in-memory snapshot).
 *
 * Procedures empty line (F7-F fix 1, bk-819): while `isProceduresTruncated`
 * is true for THIS card (section capped at 25 plans OR a loaded plan's own
 * `sittings_truncated`), every row's empty line reads "may not be shown"
 * instead of "no procedures" — a truncated plan's sittings can belong to
 * ANY visit, so no single row can be marked, but no row may assert absence
 * either. `PatientTimeline` renders the matching card-level notice from the
 * SAME shared boolean (`utils/patientCardProcedures.js`).
 *
 * Lane status (Q2, QA D3): every sub-section reads its lane through
 * `readLane` (`utils/patientCardLanes.js`) — the ONE guard — so a lane that
 * FAILED to read can never be rendered as a lane that is EMPTY. Raw
 * section item access is banned here and enforced by the static source scan
 * in `utils/__tests__/patientCardLanes.test.js`.
 */
export default function OpdTimelineRow({ visit, sections = {}, expanded, onToggle }) {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // Scroll this row's header back into view once its body finishes
  // expanding (build-plan F7: `expanding_scrolls_row_header_into_view`).
  useEffect(() => {
    if (!expanded) return;
    const header = containerRef.current
      ?.closest('[data-testid^="timeline-row-"]')
      ?.querySelector('[data-testid^="timeline-row-toggle-"]');
    header?.scrollIntoView({ block: 'nearest' });
  }, [expanded]);

  if (!expanded || !visit) return null;

  const visitId = visit.visit_id;

  // ── Prescription — selection rule (F7-F fix 2, pinned): latest
  // non-superseded DIGITAL entry WITH CONTENT → else latest non-superseded
  // FILE-BACKED entry (`s3_key` present — scanned/uploaded) → else the
  // genuine empty state. A file-backed entry is never "no prescription":
  // `MetadataEntryResponse` carries the file fields alongside `medicines`.
  // "With content" is the ONE shared rule (`utils/prescriptionContent.js`),
  // a DISPLAY question of its own and not a copy of any backend rule — NOT
  // "has medicines"
  // (bk-1148/bk-1150): RX-MIN-FIELDS ships diagnosis-only prescriptions, and
  // the old medicines-only gate made this row assert a false absence for
  // them. Digital-with-content is checked FIRST, so a digital entry can
  // never fall through into the file branch. ──────────────────────────────
  const prescriptionsLane = readLane(sections?.prescriptions);
  const { items: prescriptionEntries } = prescriptionsLane;
  const prescriptionItems = prescriptionEntries.filter((e) => e.visit_id === visitId);
  const latestDigitalRx = prescriptionItems
    .filter((e) => e.prescription_type === 'digital' && hasPrescriptionContent(e) && e.status !== 'superseded')
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] || null;
  const latestFileRx = latestDigitalRx ? null : (
    prescriptionItems
      .filter((e) => e.status !== 'superseded' && e.s3_key)
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] || null
  );

  // ── Prescription photos (owner D27) ──────────────────────────────────────
  // The photos of the prescription this row actually shows. They arrive
  // NESTED on the item (`images`, always present, `[]` when none) and already
  // ordered by `image_order` — rendered in array order, never re-sorted, and
  // never counted into anything: `total`/`returned` count prescriptions only.
  const shownRx = latestDigitalRx || latestFileRx;
  const rxImages = Array.isArray(shownRx?.images) ? shownRx.images : [];

  // The photos the server could NOT nest, because the prescription they name
  // is not in this response — today: a prescription corrected on a CLOSED
  // visit keeps its photos on the superseded version, which is filtered out.
  // `unattached_images` is THREE-STATE and the states are not interchangeable:
  //   null      ⇔ not applicable (any lane but prescriptions, or an unread one)
  //   []        ⇔ applicable, and every photo found its parent
  //   non-empty ⇔ stranded photos that live ONLY here.
  // `null` and `[]` are different facts with the same rendering (nothing);
  // `Array.isArray` is what keeps `null` from ever being walked. Scoped to
  // this visit by the `visit_id` every metadata row carries — the one thing
  // the data DOES say about them — so the group can neither repeat on every
  // row of the card nor imply a parent prescription it cannot name.
  const unattachedImages = Array.isArray(sections?.prescriptions?.unattached_images)
    ? sections.prescriptions.unattached_images.filter((img) => img.visit_id === visitId)
    : [];

  // ── Procedures — sittings, across every plan, performed on THIS visit.
  // A plan past the fan-out cap carries `sittings: null` (never `[]`), which
  // `Array.isArray` already excludes correctly — this row simply cannot
  // attribute those sittings to any visit. `proceduresTruncated` (below)
  // is what stops this row from then falsely claiming there are none. ────
  const proceduresLane = readLane(sections?.procedures);
  const { items: plans } = proceduresLane;
  const sittings = plans
    .flatMap((plan) => (Array.isArray(plan.sittings) ? plan.sittings : []))
    .filter((s) => s.visit_id === visitId);
  const proceduresTruncated = isProceduresTruncated(sections?.procedures);

  // ── Labs — server-resolved `context`, never an inferred visit-id shape
  // (build-plan F7 data rule). ─────────────────────────────────────────────
  const labsLane = readLane(sections?.labs);
  const { items: labItems } = labsLane;
  const labOrders = labItems
    .filter((o) => o.context?.kind === LAB_CONTEXT_KIND.OPD_VISIT && o.context?.ref_id === visitId);

  // ── Multi-doctor / referred visit — keep the existing attending-doctor
  // grouping badge (Phase 3.1), unchanged from `PatientHistory.jsx`. ──────
  const { isMultiDoctor, segments } = groupVisitEntriesByAttendingDoctor(visit);

  // Rebuilds the saved prescription's document and opens it. Named for what
  // the reader is doing — viewing a record — not for what the transport does.
  const handleViewPdf = async () => {
    setPdfGenerating(true);
    try {
      await openPrescriptionPdf(visitId);
    } finally {
      setPdfGenerating(false);
    }
  };

  // Explicit-click-only file open (same shape as `PatientHistory.jsx`'s
  // `handleViewFile` / `handleViewPdf` above) — never fired on mount or
  // expand, only from the `FileRow`'s "View" button.
  // `title` distinguishes a prescription FILE from a prescription PHOTO in the
  // viewer chrome; the route is the same per-entry download either way, keyed
  // on the entry's own `meta_type` (PRESCRIPTION / PRESCRIPTION_IMAGE).
  const handleViewFile = async (entry, title = 'Prescription') => {
    try {
      const { blobUrl, contentType } = await downloadFileSecure(visitId, entry.meta_type, entry.meta_id);
      const fileType = contentType?.startsWith('image') ? 'image' : 'pdf';
      useFileViewerStore.getState().openViewer({ blobUrl, fileType, title });
    } catch (err) {
      console.error('Failed to view prescription file:', err);
    }
  };

  return (
    <div ref={containerRef} data-testid={`opd-row-body-${visitId}`} className="space-y-1">
      {isMultiDoctor && (
        <div className="inline-flex items-center gap-1 flex-wrap rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold bg-orange-50 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 text-orange-800 dark:text-orange-200">
          <span>🟠</span>
          {segments.map((seg, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span>→</span>}
              <span>{resolveSegmentDoctorName(seg, visit, [])}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      {latestDigitalRx ? (
        // PrescriptionReadOnlyView renders its own "Prescription" title bar —
        // no outer HistorySection wrapper (that would double the heading, the
        // way Procedures/Labs never do since they self-title internally too).
        <PrescriptionReadOnlyView
          prescriptionEntry={latestDigitalRx}
          // SHOWN on the four-term content rule, PRINTABLE on the SERVER's
          // per-entry answer (owner D28). A legacy diagnosis-only Rx is worth
          // showing but `/pdf` 400s for it — so the download button is
          // withheld rather than offered and failed. This row shows exactly
          // ONE entry, so the per-entry flag is the matching granularity.
          // `=== true`, never `!== false`: `printable` is absent on legacy
          // rows and null on non-prescription rows, and neither is printable.
          // The bk-861/D24 decision that this read-only card does not
          // re-check `GENERATE_PRESCRIPTION_PDF` is unchanged.
          canGeneratePdf={latestDigitalRx.printable === true}
          onGeneratePdf={handleViewPdf}
          pdfGenerating={pdfGenerating}
        />
      ) : latestFileRx ? (
        // Scanned/uploaded prescription — the record IS present, it just
        // wasn't typed. Same FileRow + FileViewerModal path PatientHistory
        // uses for these entries; only a user click ever fetches the file.
        <HistorySection title="💊 Prescription" isDark={isDark}>
          <FileRow
            entry={latestFileRx}
            isDark={isDark}
            formatSize={formatFileSize}
            onView={() => handleViewFile(latestFileRx)}
          />
        </HistorySection>
      ) : (
        <LaneSubsection
          title="💊 Prescription"
          isDark={isDark}
          lane={prescriptionsLane}
          laneName="prescriptions"
          emptyText="No prescription recorded for this visit."
        />
      )}

      {/* The shown prescription's own photos (D27) — WITH the prescription
          they belong to, immediately under it, not in a general files list.
          No band at all when it has none. */}
      {rxImages.length > 0 && (
        <HistorySection title={PHOTOS_HEADING} isDark={isDark}>
          <PrescriptionPhotoRows
            images={rxImages}
            isDark={isDark}
            onView={(image) => handleViewFile(image, 'Prescription photo')}
          />
        </HistorySection>
      )}

      {/* The orphans — photos of THIS visit whose prescription is not in the
          record. Shown, because they exist nowhere else; and shown as what
          they are. The copy asserts only the two things the data carries:
          the visit they were added on, and that no prescription here claims
          them. No parent is invented and none is implied. */}
      {unattachedImages.length > 0 && (
        <HistorySection title={UNLINKED_PHOTOS_HEADING} isDark={isDark}>
          <p className={HINT_LINE_CLASS} style={{ marginBottom: '0.375rem' }}>
            {UNLINKED_PHOTOS_HINT}
          </p>
          <PrescriptionPhotoRows
            images={unattachedImages}
            isDark={isDark}
            onView={(image) => handleViewFile(image, 'Prescription photo')}
          />
        </HistorySection>
      )}

      {sittings.length > 0 ? (
        <VisitProceduresHistorySection
          sittings={sittings}
          isDark={isDark}
          visitId={visitId}
          visitDate={visit.visit_date}
        />
      ) : (
        <LaneSubsection
          title="🦷 Procedures"
          isDark={isDark}
          lane={proceduresLane}
          laneName="procedures"
          emptyText={proceduresTruncated
            ? 'Procedures for this visit may not be shown.'
            : 'No procedures recorded for this visit.'}
        />
      )}

      {labOrders.length > 0 ? (
        <VisitLabHistorySection orders={labOrders} isDark={isDark} reportOnly />
      ) : (
        <LaneSubsection
          title="🧪 Lab tests"
          isDark={isDark}
          lane={labsLane}
          laneName="labs"
          emptyText="No lab orders for this visit."
        />
      )}
    </div>
  );
}
